import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { layoutFor, planBarcodes, usePrinter, TSPL_FONTS } from "@/context/PrinterContext";

/**
 * Visual preview of how a label will print at the configured physical
 * size, with live font-size controls. We use the *same* `layoutFor()`
 * math the TSPL builders use, so the preview is faithful to byte-for-byte
 * what the printer renders — any layout drift here would mean the printer
 * also drifts.
 */
const FONT_DOT_HEIGHT = Object.fromEntries(TSPL_FONTS.map((f) => [f.id, f.dots]));
// Sentinel value for "use the auto tier-based pick" — Radix Select can't
// hold an empty string, so we round-trip null through this placeholder.
const AUTO_VALUE = "__auto__";

const SAMPLE_PACKING = {
  recipe_name: "Sourdough Loaf",
  batch_code: "BAT-20260623-0042",
  packs_produced: "12",
  pack_unit: "boxes",
  packed_at: "2026-06-23",
  allergens: ["Gluten", "Wheat", "Sesame"],
  label_code: "PK-DEMO-0001",
};
const SAMPLE_INTAKE = {
  material_name: "Strong White Flour",
  internal_batch_code: "INT-20260623-0010",
  supplier_batch: "SUP-LOT-554211",
  expiry_date: "2026-12-31",
  quantity: "25",
  unit: "kg",
};

function BarcodePlaceholder({ width, height, value }) {
  return (
    <>
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width,
          height: Math.max(0, height - 14),
          background:
            "repeating-linear-gradient(90deg, #111 0 2px, transparent 2px 4px, #111 4px 5px, transparent 5px 8px)",
        }}
        title={`Code 128 placeholder for ${value}`}
      />
      {/* Human-readable text rendered by the printer below the bars. */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: Math.max(0, height - 12),
          width,
          textAlign: "center",
          fontSize: 10,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
          color: "#0a0a0a",
          letterSpacing: "0.5px",
        }}
      >
        {value}
      </div>
    </>
  );
}

function PreviewLabel({ widthMm, heightMm, kind, fontOverrides }) {
  const data = kind === "intake" ? SAMPLE_INTAKE : SAMPLE_PACKING;
  const L = useMemo(
    () => layoutFor(widthMm, heightMm, fontOverrides),
    [widthMm, heightMm, fontOverrides],
  );

  const W = widthMm * 8;
  const H = heightMm * 8;
  const MAX = 320;
  const scale = Math.min(MAX / W, (MAX * 1.2) / H, 1.4);
  const pxW = W * scale;
  const pxH = H * scale;

  const fontPx = (tspl) => Math.max(8, FONT_DOT_HEIGHT[tspl] * scale * 0.78);

  const rows =
    kind === "intake"
      ? [
          { y: L.titleY, sizeId: L.titleId, text: "Oz Bakery · Intake" },
          { y: L.productY, sizeId: L.productId, text: data.material_name },
          { y: L.detailStart, sizeId: L.detailId, text: `Int: ${data.internal_batch_code}` },
          { y: L.detailStart + L.lineGap, sizeId: L.detailId, text: `Sup: ${data.supplier_batch}` },
          { y: L.detailStart + L.lineGap * 2, sizeId: L.detailId, text: `BBE: ${data.expiry_date}` },
          ...(L.showAllergens
            ? [{ y: L.detailStart + L.lineGap * 3, sizeId: L.detailId, text: `Qty: ${data.quantity} ${data.unit}` }]
            : []),
        ]
      : [
          { y: L.titleY, sizeId: L.titleId, text: "Oz Bakery" },
          { y: L.productY, sizeId: L.productId, text: data.recipe_name },
          { y: L.detailStart, sizeId: L.detailId, text: `Batch: ${data.batch_code}` },
          { y: L.detailStart + L.lineGap, sizeId: L.detailId, text: `Packs: ${data.packs_produced} ${data.pack_unit}` },
          { y: L.detailStart + L.lineGap * 2, sizeId: L.detailId, text: `Packed: ${data.packed_at}` },
          ...(L.showAllergens && data.allergens?.length
            ? [{ y: L.detailStart + L.lineGap * 3, sizeId: L.detailId, text: `Contains: ${data.allergens.join(",")}` }]
            : []),
        ];

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="text-[11px] uppercase tracking-wider text-stone-500 font-bold">
        {kind === "intake" ? "Intake label" : "Packing label"}
      </div>
      <div
        data-testid={`label-preview-${kind}`}
        className="relative bg-white border border-stone-300 shadow-sm"
        style={{
          width: pxW,
          height: pxH,
          boxShadow: "inset 0 0 0 1px #f5f5f4",
        }}
      >
        {rows.map((r) => (
          <div
            // text is a stable id within a single label (rows never share text)
            key={r.text}
            style={{
              position: "absolute",
              left: L.margin * scale,
              top: r.y * scale,
              fontSize: fontPx(r.sizeId),
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
              fontWeight: FONT_DOT_HEIGHT[r.sizeId] >= 48 ? 700 : 500,
              lineHeight: 1,
              color: "#0a0a0a",
              whiteSpace: "nowrap",
              maxWidth: pxW - L.margin * scale * 2,
              overflow: "hidden",
              textOverflow: "clip",
            }}
          >
            {r.text}
          </div>
        ))}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: pxW,
            height: pxH,
          }}
        >
          {(() => {
            const barcodeData =
              kind === "intake" ? data.internal_batch_code : data.label_code;
            const plan = planBarcodes(L, widthMm, String(barcodeData || "").length);
            const barW = plan.code128W * scale;
            return (
              <div
                style={{
                  position: "absolute",
                  left: plan.code128X * scale,
                  top: plan.code128Y * scale,
                  width: barW,
                  height: L.barcodeH * scale,
                }}
              >
                <BarcodePlaceholder
                  width={barW}
                  height={L.barcodeH * scale}
                  value={barcodeData}
                />
              </div>
            );
          })()}
        </div>
      </div>
      <div className="text-[10px] text-stone-500 tnum">
        {widthMm} × {heightMm} mm · scale 1:
        {(1 / scale).toFixed(2).replace(/\.?0+$/, "")}
      </div>
    </div>
  );
}

/**
 * Single font-picker row. Three of these are stacked in the dialog —
 * one each for the small "Oz Bakery" title, the bold product name, and
 * the detail rows (Batch / Packs / Packed / Contains).
 *
 * `value` is `null` when the user hasn't overridden — Radix can't bind
 * to null, so we map null ↔ AUTO_VALUE at the boundary.
 */
function FontPicker({ id, label, hint, value, onChange }) {
  return (
    <div data-testid={`font-picker-${id}`}>
      <Label className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">
        {label}
      </Label>
      <Select
        value={value || AUTO_VALUE}
        onValueChange={(v) => onChange(v === AUTO_VALUE ? null : v)}
      >
        <SelectTrigger className="h-9 mt-1" data-testid={`font-select-${id}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={AUTO_VALUE} data-testid={`font-${id}-auto`}>
            Auto (size-based)
          </SelectItem>
          {TSPL_FONTS.map((f) => (
            <SelectItem
              key={f.id}
              value={f.id}
              data-testid={`font-${id}-${f.id}`}
            >
              {f.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hint && (
        <div className="text-[10px] text-stone-400 mt-1">{hint}</div>
      )}
    </div>
  );
}

/**
 * Modal preview the operator opens from the printer popover. Shows
 * both the packing label and the intake label rendered to scale at the
 * currently configured size, with live font-size controls for the
 * three text roles. Changes persist via the printer context's
 * `setFontOverrides` so the next real print uses them.
 */
export default function LabelPreviewDialog({ open, onOpenChange, size }) {
  const printer = usePrinter();
  if (!size || !printer) return null;
  const { fontOverrides, setFontOverrides } = printer;

  const update = (role, val) =>
    setFontOverrides({ ...fontOverrides, [role]: val });

  const allAuto =
    !fontOverrides?.title && !fontOverrides?.product && !fontOverrides?.detail;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="label-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display">
            Label preview — {size.width} × {size.height} mm
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-stone-500 mb-3">
          Adjust each role&apos;s font below — preview updates live and
          your choice is sent to the printer on the next print. Pick
          &ldquo;Auto&rdquo; to let the app size by label dimensions.
        </div>

        <div className="grid grid-cols-3 gap-3 mb-5" data-testid="font-controls">
          <FontPicker
            id="title"
            label="Title (Oz Bakery)"
            value={fontOverrides?.title}
            hint="Top header line"
            onChange={(v) => update("title", v)}
          />
          <FontPicker
            id="product"
            label="Product name"
            value={fontOverrides?.product}
            hint="Recipe / material — biggest line"
            onChange={(v) => update("product", v)}
          />
          <FontPicker
            id="detail"
            label="Detail rows"
            value={fontOverrides?.detail}
            hint="Batch, dates, allergens"
            onChange={(v) => update("detail", v)}
          />
        </div>

        {!allAuto && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="mb-3"
            data-testid="font-reset-auto"
            onClick={() =>
              setFontOverrides({ title: null, product: null, detail: null })
            }
          >
            Reset all to Auto
          </Button>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 place-items-center">
          <PreviewLabel
            widthMm={size.width}
            heightMm={size.height}
            kind="packing"
            fontOverrides={fontOverrides}
          />
          <PreviewLabel
            widthMm={size.width}
            heightMm={size.height}
            kind="intake"
            fontOverrides={fontOverrides}
          />
        </div>

        <div className="mt-4 text-[11px] text-stone-500">
          Need a different label size? Close this and use the size dropdown
          / Custom… inputs in the printer chip.
        </div>
      </DialogContent>
    </Dialog>
  );
}
